import { useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { API_BASE } from "../lib/api";

export default function Challenge() {
  const { code } = useParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onContinue = async () => {
    if (busy || !code) return;
    setBusy(true);
    setError(null);
    try {
      const host = window.location.hostname;
      const res = await fetch(
        `${API_BASE}/preview/${encodeURIComponent(code)}?host=${encodeURIComponent(host)}`
      );
      if (!res.ok) {
        setError("We couldn’t verify this link. Try refreshing.");
        setBusy(false);
        return;
      }
      const data = await res.json();
      if (data.access_status === "bot_challenge") {
        setError("Your browser still looks automated. Try a different browser.");
        setBusy(false);
        return;
      }
      if (data.access_status === "geo_blocked") {
        window.location.replace(`/blocked/${encodeURIComponent(code)}`);
        return;
      }
      if (data.access_status && data.access_status !== "ok") {
        window.location.replace(`/unlock/${encodeURIComponent(code)}`);
        return;
      }
      if (!data.url) {
        setError("This link is unavailable.");
        setBusy(false);
        return;
      }
      // Record the click on the way out (fire-and-forget).
      fetch(`${API_BASE}/links/${encodeURIComponent(code)}/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "challenge",
          referrer: document.referrer || null,
          host,
        }),
        keepalive: true,
      }).catch(() => {});
      if (!data.url.startsWith("https://") && !data.url.startsWith("http://")) {
        setError("This link is unavailable.");
        setBusy(false);
        return;
      }
      window.location.replace(data.url);
    } catch (e) {
      setError("Verification failed. Try again.");
      setBusy(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
      data-testid="challenge-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-lg md:p-10">
        <div className="mb-6 flex justify-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100">
            <ShieldCheck size={22} strokeWidth={2.25} className="text-neutral-700" />
          </div>
        </div>
        <h1 className="text-xl font-semibold text-neutral-900 md:text-2xl">Quick check</h1>
        <p className="mt-2 text-sm text-neutral-500">
          Confirm you’re a human to continue to the link.
        </p>
        <button
          type="button"
          onClick={onContinue}
          disabled={busy}
          data-testid="challenge-continue-btn"
          className="mt-6 w-full rounded-lg bg-neutral-900 py-2.5 font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Verifying…" : "Continue"}
        </button>
        {error ? (
          <p className="mt-3 text-sm text-red-600" data-testid="challenge-error">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
